import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import { useActingClientAuthorizedCheck } from 'components/patient/useActingClientAuthorizedCheck';
import { useReferralId } from 'hooks/useReferralId';
import { useReview } from 'hooks/useReview';
import Referral from 'models/reviews/Referral';
import { referralQueryKeys, referralQueryParams } from 'services/api/reviews';

type ReferralContextType = {
  referral?: Referral;
  referralLoading: boolean;
  setSelectedReferralId: (referralId: string) => void;
};

const ReferralContext = createContext<ReferralContextType>({
  referral: undefined,
  referralLoading: true,
  setSelectedReferralId: () => {},
});

export function ReferralProvider({
  children,
  scopeAuthorization = false,
}: {
  children: React.ReactNode;
  scopeAuthorization?: boolean;
}) {
  const referralId = useReferralId();
  const queryClient = useQueryClient();
  const [selectedReferralId, setSelectedReferralId] = useState<string>(referralId ?? '');

  useEffect(() => {
    if (referralId) {
      setSelectedReferralId(referralId);
    }
  }, [referralId, setSelectedReferralId]);

  // Refetch the referrals list under the newly-scoped account when the acting client switches.
  const handleClientSwitched = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: [...referralQueryKeys.base, 'index'] });
    queryClient.invalidateQueries({ queryKey: ['referralsCount'] });
  }, [queryClient]);

  // Scope to a client that can access the referral before fetching it — the show endpoint 404s otherwise.
  const authorizationChecked = useActingClientAuthorizedCheck(
    scopeAuthorization ? selectedReferralId || undefined : undefined,
    {
      resourceType: 'referral',
      onClientSwitched: handleClientSwitched,
      fallbackPath: '/referrals',
      notFoundText: 'Referral not found',
    }
  );

  const { review, loading } = useReview(selectedReferralId, referralQueryKeys, referralQueryParams, {
    enabled: scopeAuthorization ? authorizationChecked : true,
  });

  const value = useMemo(
    () => ({
      referral: review as Referral,
      referralLoading: loading,
      setSelectedReferralId,
    }),
    [review, loading]
  );

  return <ReferralContext value={value}>{children}</ReferralContext>;
}

export function useReferral() {
  const context = useContext(ReferralContext);

  if (!context) {
    throw new Error('useReferral must be used within a ReferralProvider');
  }
  return context;
}
